import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Dropdown, Input, Select, Table, Tooltip } from 'antd';
import type { MenuProps, TableProps } from 'antd';
import { MoreHorizontal, Truck } from 'lucide-react';
import { useState } from 'react';
import { VEHICLE_TYPE_SORT_FIELDS, type Paginated, type VehicleTypeDto, type VehicleTypeSortField } from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { FilterField } from '@/components/FilterField';
import { NameCell } from '@/components/NameCell';
import { PageHeader } from '@/components/PageHeader';
import { StatusDot } from '@/components/StatusDot';
import { useAuth } from '@/hooks/useAuth';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { columnKeySet } from '@/lib/tableColumns';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useServerTable } from '@/hooks/useServerTable';
import { can, READ_ONLY_TOOLTIP } from '@/lib/accessRole';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { VehicleTypeDrawer } from './components/VehicleTypeDrawer';

/** The mono chip idiom from `OrgUnitDetail`, for a code that is read rather than prose. */
const CODE_CHIP = 'rounded bg-secondary px-1.5 py-0.5 font-mono text-xs text-muted-foreground';

/**
 * The vehicle type master.
 *
 * ⚠️ The wildcard column reads `isWildcard`, NEVER `code === 'ANY'`. The seeded wildcard
 * is coded `ANY` but named "Any vehicle" precisely so a name comparison fails on the seed
 * data — see `services/vehicleType.service.ts`.
 *
 * `useServerTable`'s sixth consumer, again with no change to the hook.
 */
const VehicleTypesPage = () => {
  const { officer } = useAuth();
  const queryClient = useQueryClient();
  /**
   * ⚠️ `modal` FROM `App.useApp()`, NOT THE STATIC `Modal.confirm`.
   *
   * Two reasons, and the second was measured. CLAUDE.md §8 already records that AntD
   * statics always use the ROOT theme and escape a `ThemeScope`. And the static does not
   * render AT ALL under jsdom — which is why no confirmation dialog in this repository
   * had a test before P2. The hook form renders, is themed, and is assertable.
   */
  const { message, modal } = App.useApp();
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; vehicleType?: VehicleTypeDto }>();

  const canWrite = officer ? can(officer.accessRole, 'vehicleType.write') : false;

  const table = useServerTable<VehicleTypeDto, VehicleTypeSortField>({
    sortFields: VEHICLE_TYPE_SORT_FIELDS,
    defaultSortBy: 'displayOrder',
    defaultSortOrder: 'asc',
    filterKeys: ['show'],
    queryKey: (params) => queryKeys.vehicleTypes.list(params),
    queryFn: (params, signal) =>
      api.get<Paginated<VehicleTypeDto>>('/vehicle-types', {
        query: {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          search: params.search,
          ...params.filters,
        },
        signal,
      }),
  });

  /*
   * ⚠️ `|| 'current'` — `useServerTable` STRIPS an empty filter value so it never reaches
   * the URL, which is what keeps a shared link clean. The page therefore re-defaults on the
   * way out, exactly as `pages/Arrangements` does for `kind`.
   */
  const show = table.params.filters.show || 'current';

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.vehicleTypes.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.policies.all });
  };

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.post<VehicleTypeDto>(`/vehicle-types/${id}/${active ? 'restore' : 'archive'}`),
    onSuccess: (_data, variables) => {
      void message.success(variables.active ? 'Vehicle type restored' : 'Vehicle type archived');
      invalidate();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const confirmArchive = (vehicleType: VehicleTypeDto) =>
    modal.confirm({
      title: `Archive ${vehicleType.name}?`,
      content:
        'Archiving is used instead of deletion. Policy versions that already reference this type keep working; it stops ' +
        'appearing in NEW ones.',
      okText: 'Archive',
      okButtonProps: { danger: true },
      onOk: () => void setActive.mutateAsync({ id: vehicleType.id, active: false }),
    });

  const rowActions = (vehicleType: VehicleTypeDto): MenuProps['items'] =>
    vehicleType.archivedAt === null
      ? [
          {
            key: 'archive',
            label: 'Archive',
            danger: true,
            disabled: !canWrite,
            onClick: () => void confirmArchive(vehicleType),
          },
        ]
      : [
          {
            key: 'restore',
            label: 'Restore',
            disabled: !canWrite,
            onClick: () => void setActive.mutateAsync({ id: vehicleType.id, active: true }),
          },
        ];

  const allColumns: TableProps<VehicleTypeDto>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name: string, row) => (
        <div className="flex min-w-0 items-center gap-2">
          <span className={CODE_CHIP}>{row.code}</span>
          {/* The first column opens the row — see `NameCell`. The code chip stays inert
              beside it: it is an identifier, not a control. */}
          <NameCell name={name} onOpen={() => setEditing({ mode: 'edit', vehicleType: row })} />
        </div>
      ),
    },
    {
      title: 'Matching',
      key: 'isWildcard',
      width: 160,
      render: (_value, row) =>
        // Read off the FLAG. A future matcher must do the same.
        row.isWildcard ? <StatusDot tone="info" label="Any vehicle" /> : <span className="text-subtle">—</span>,
    },
    {
      title: 'Order',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      align: 'right',
      width: 90,
      render: (order: number) => <span className="tabular-nums">{order}</span>,
    },
    {
      title: 'Status',
      /*
       * ⚠️ [LIFECYCLE] `dataIndex` MOVED WITH THE COLUMN, and it had to. It read `isActive`,
       * which no longer exists on the DTO — so `render` would have received `undefined` and
       * drawn EVERY row as archived, with no type error, because `dataIndex` is a string.
       *
       * The `key` moves too, from `isActive` to `lifecycle`. `useColumnVisibility` stores the
       * HIDDEN set by key, so a stored preference naming the old key simply stops matching
       * and the column becomes visible again — the safe direction, and the reason the hook
       * keys on hidden rather than on shown.
       */
      dataIndex: 'archivedAt',
      key: 'lifecycle',
      width: 120,
      render: (archivedAt: string | null) =>
        archivedAt === null ? (
          <StatusDot tone="success" label="Active" />
        ) : (
          <StatusDot tone="neutral" label="Archived" />
        ),
    },
    {
      title: '',
      key: 'actions',
      // [P4-UX] Pinned: an action a scroll can hide is an action that is not there.
      fixed: 'right' as const,
      align: 'right',
      width: 120,
      render: (_value, row) => (
        <div className="flex items-center justify-end gap-1">
          <Tooltip title={canWrite ? undefined : READ_ONLY_TOOLTIP}>
            <Button
              type="text"
              size="small"
              disabled={!canWrite}
              onClick={() => setEditing({ mode: 'edit', vehicleType: row })}
            >
              Edit
            </Button>
          </Tooltip>
          <Dropdown menu={{ items: rowActions(row) }} trigger={['click']} placement="bottomRight">
            <Button type="text" size="small" aria-label={`More actions for ${row.name}`}>
              <MoreHorizontal className="size-4" aria-hidden />
            </Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  /*
   * ⚠️ THE MENU GETS THE UNFILTERED ARRAY; THE TABLE GETS THE FILTERED ONE. That is the
   * circularity solved — by never letting the two be the same variable. Keys and locks are
   * DERIVED from the array above, so this page keeps no second list of columns.
   */
  const columnKeys = columnKeySet(allColumns ?? []);
  const columnVisibility = useColumnVisibility({ ...columnKeys, storageKey: 'vehicle-types' });
  const columns = (allColumns ?? []).filter((column) => columnVisibility.isVisible(String(column.key)));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={Truck}
        title="Vehicle types"
        subtitle={`${table.total} — what a policy's vehicle block can ask for`}
        actions={
          <Tooltip title={canWrite ? undefined : READ_ONLY_TOOLTIP}>
            <Button type="primary" disabled={!canWrite} onClick={() => setEditing({ mode: 'create' })}>
              New vehicle type
            </Button>
          </Tooltip>
        }
        filters={
          <>
            {/*
              ⚠️ [LIFECYCLE] TWO AXES, TWO CONTROLS — never one mixed dropdown.

              `Show` says whether a row is in circulation; a status filter (where one exists)
              says where it is in its own life. Collapsing them into one list would make
              "Archived" look like a peer of "Draft", which is exactly the confusion the
              archive-is-an-axis design exists to prevent.

              ⚠️ A `Select`, NOT a `Segmented`. `Segmented` is this repository's TWO-value
              switch and `pages/Arrangements` says so in prose; three values stop it being a
              switch, and on `/arrangements` it would sit beside the kind Segmented and read
              as a second tab bar.

              ⚠️ NO `allowClear`. "Current" is a VALUE, not the absence of a filter — clearing
              would emit `undefined` and the page would have to re-default it.
            */}
            <FilterField label="Show" className="w-44">
              <Select
                className="w-full"
                value={show}
                onChange={(value: string) => table.setFilter('show', value === 'current' ? undefined : value)}
                options={[
                  { value: 'current', label: 'Current' },
                  { value: 'archived', label: 'Archived' },
                  { value: 'all', label: 'All' },
                ]}
                data-testid="show-filter"
              />
            </FilterField>
            <FilterField label="Search" className="w-64">
              <Input.Search
                allowClear
                placeholder="Name"
                value={table.searchInput}
                onChange={(event) => table.onSearchChange(event.target.value)}
                data-testid="vehicle-type-search"
              />
            </FilterField>
            {/* The Columns menu sits IN the filter row, not in `actions`: it is a view control,
                the same argument `pages/Arrangements` makes for its Segmented. */}
            <ColumnsMenu
              className="ml-auto"
              columns={allColumns ?? []}
              visibility={columnVisibility}
              locked={columnKeys.locked}
            />
          </>
        }
      />

      <Table<VehicleTypeDto>
        {...table.tableProps}
        columns={columns}
        size="middle"
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: (
            <EmptyState
              size="sm"
              title={table.searchInput ? 'No vehicle types match this search.' : 'No vehicle types yet.'}
            />
          ),
        }}
      />

      {editing && (
        <VehicleTypeDrawer
          open
          mode={editing.mode}
          {...(editing.vehicleType ? { vehicleType: editing.vehicleType } : {})}
          readOnly={!canWrite}
          onClose={() => setEditing(undefined)}
          onSuccess={invalidate}
        />
      )}
    </div>
  );
};

export default VehicleTypesPage;
